import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ShowHeader } from "@/common/components/ShowHeader";
import { useCompanyBranch } from "@/common/hooks/queries/useCompanyBranch";
import { ConsumptionRestriction } from "@/features/company/components/Profile/ConsumptionRestriction";
import { PeriodRestriction } from "@/features/company/components/Profile/PeriodRestriction";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { CreditConfiguration } from "@/features/companyBranches/components/show/CreditConfiguration";
import { BriefcaseBusinessIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { BranchInformation } from "../../components/show/BranchInformation";
import { GeneralInformation } from "../../components/show/GeneralInformation";
import { MasterClientUser } from "../../components/show/MasterClientUser";
import CompanyBranchUserAccesses from "../../components/show/CompanyBranchUserAccesses";
import { useCurrentTabInfo } from "@/common/hooks/useCurrentTabInfo";
import Tabs from "@/common/components/Tabs";

const CompanyBranchesShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: companyData,
    isPending: companyDataIsPending,
  } = useMineCompany();

  const {
    data: branchData,
    isPending: branchIsPending,
    error: branchError = {} as any,
    refetch: refetchBranch,
  } = useCompanyBranch(id || '', true, 1);

  const { currentTab } = useCurrentTabInfo([
    { value: '' },
    { value: 'user-branch-access' },
  ]);

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={branchError?.status}
      refetchFunction={refetchBranch}
    >
      <ShowHeader
        backTo="/company-branches"
        LogoPlaceholder={
          <ImageWithPlaceholder
            className="size-8"
            iconClassName="size-6"
            placeholderIcon={BriefcaseBusinessIcon}
            url={branchData?.general_information?.company_logo?.url}
            alt={branchData?.general_information?.fantasy_name}
          />
        }
        title={branchData?.display_name}
        buttons={!currentTab && (
          <Button
            type="button"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            onClick={() => navigate(`/company-branches/${id}/edit`)}
            disabled={branchIsPending || branchError?.status === 403}
            name="update_branch"
          >
            Editar
          </Button>
        ) || undefined}
      />

      <Tabs
        tabsListClassName="w-full bg-transparent rounded-none"
        tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
        tabsContentClassName="mt-6"
        isLoading={companyDataIsPending}
        skeletonsCount={2}
        tabsList={[
          {
            value: '',
            label: 'Dados gerais',
            body: (
              <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-200px)]">
                <p className="text-lg font-bold">
                  Dados da filial
                </p>

                <BranchInformation
                  displayName={branchData?.display_name}
                  parentId={branchData?.parent_id}
                  parentDisplayName={branchData?.parent_display_name}
                />

                <GeneralInformation
                  generalInformation={branchData?.general_information}
                  parentId={branchData?.parent_id}
                />

                <MasterClientUser
                  masterClientUser={branchData?.master_client_user}
                />

                <CreditConfiguration
                  branchData={branchData}
                  companyData={companyData}
                />

                {(Number(branchData?.restriction.minimum_kilometers_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.maximum_kilometers_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.minimum_seconds_interval_between_consumptions) > 0 ||
                  Number(branchData?.restriction.minimum_seconds_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.maximum_seconds_usage_between_consumptions) > 0 ||
                  Number(branchData?.restriction.product_category_restrictions?.length) > 0) && (
                    <ConsumptionRestriction restriction={branchData?.restriction} />
                  )}

                {Number(branchData?.restriction.period_restrictions?.length) > 0 && (
                  <PeriodRestriction
                    periodRestrictions={branchData?.restriction.period_restrictions}
                  />
                )}
              </Box>
            ),
          },
          {
            value: 'user-branch-access',
            label: 'Permissão de usuários',
            hidden: companyData?.branch_hierarchy_for_user_access !== 'dynamic',
            operationKlassToCheckPermission: 'Client::UserBranchAccesses::Filter',
            body: <CompanyBranchUserAccesses companyBranch={branchData} />
          },
        ]}
      />
    </ErrorMessageWrapper>
  );
};

export default CompanyBranchesShow;
